import { apiUrl } from "../config";
import { createWalletProof } from "../utils/walletProof";

function normalizeWallet(value) {
  return String(value || "").trim().toLowerCase();
}

async function readJson(response) {
  return response.json().catch(() => ({}));
}

async function postUserMetadata(target, walletProof, signal) {
  const response = await fetch(apiUrl("user"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    signal,
    body: JSON.stringify({
      wallet: target,
      ...(walletProof ? { wallet_proof: walletProof } : {}),
    }),
  });
  return { response, payload: await readJson(response) };
}

export async function fetchUserMetadata(accountId, { selector, signal, targetWallet } = {}) {
  const requester = normalizeWallet(accountId);
  const target = normalizeWallet(targetWallet || requester);
  if (!requester) throw new Error("Wallet is required");
  if (!selector) throw new Error("Wallet is not connected");

  let { response, payload } = await postUserMetadata(target, null, signal);
  if (response.status === 401) {
    const walletProof = await createWalletProof({ selector, accountId: requester, signal });
    ({ response, payload } = await postUserMetadata(target, walletProof, signal));
  }
  if (!response.ok) {
    const error = new Error(payload?.error || `User metadata unavailable (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return payload;
}

async function postUserLanguage(wallet, language, walletProof, signal) {
  const response = await fetch(apiUrl("user/language"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    signal,
    body: JSON.stringify({
      wallet,
      language,
      ...(walletProof ? { wallet_proof: walletProof } : {}),
    }),
  });
  return { response, payload: await readJson(response) };
}

export async function updateUserLanguage(accountId, language, { selector, signal } = {}) {
  const requester = normalizeWallet(accountId);
  const normalizedLanguage = String(language || "").trim();
  if (!requester) throw new Error("Wallet is required");
  if (!normalizedLanguage) throw new Error("Language is required");

  let { response, payload } = await postUserLanguage(requester, normalizedLanguage, null, signal);
  if (response.status === 401) {
    if (!selector) throw new Error("Wallet is not connected");
    const walletProof = await createWalletProof({ selector, accountId: requester, signal });
    ({ response, payload } = await postUserLanguage(requester, normalizedLanguage, walletProof, signal));
  }
  if (!response.ok) {
    const error = new Error(payload?.error || `Language update failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return payload;
}
